<template>
  <v-dialog
    v-model="dialogProxy"
    :max-width="showPreview ? 'calc(100% - 80px)' : '1280px'"
    persistent
    scrollable
  >
    <v-card>
      <v-card-title class="headline d-flex align-center">
        <span>Sales Contract</span>
        <v-spacer />

        <v-btn
          v-if="showPreview"
          icon
          class="mr-2"
          @click="showPreview = false"
        >
          <v-icon>mdi-eye-off</v-icon>
        </v-btn>

        <v-btn icon @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-0">
        <div class="contract-dialog-layout">
          <div :class="['contract-form-pane', showPreview ? 'with-preview' : '']">
            <div class="pa-6">
              <div class="section-title mb-2">Customer Information</div>

              <v-row dense>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.contract_number"
                    label="Contract / SO Number"
                    outlined
                    dense
                    :class="{ 'optional-warning-field': hasOptionalWarning('contract_number') }"
                    @input="clearOptionalWarning('contract_number')"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.customer_tax_number"
                    label="Tax number"
                    outlined
                    dense
                    :class="{ 'optional-warning-field': hasOptionalWarning('customer_tax_number') }"
                    @input="clearOptionalWarning('customer_tax_number')"
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.customer_name"
                    label="Customer Name *"
                    outlined
                    dense
                    :error-messages="getValidationError('customer_name')"
                    @input="clearValidationError('customer_name')"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.customer_representative"
                    label="Representative"
                    outlined
                    dense
                    :class="{ 'optional-warning-field': hasOptionalWarning('customer_representative') }"
                    @input="clearOptionalWarning('customer_representative')"
                  />
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="form.customer_address"
                    label="Billing address *"
                    outlined
                    dense
                    rows="2"
                    auto-grow
                    :error-messages="getValidationError('customer_address')"
                    @input="clearValidationError('customer_address')"
                  />
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="form.shipping_address"
                    label="Shipping address"
                    outlined
                    dense
                    rows="2"
                    auto-grow
                    :class="{ 'optional-warning-field': hasOptionalWarning('shipping_address') }"
                    @input="clearOptionalWarning('shipping_address')"
                  />
                </v-col>
              </v-row>

              <div class="section-title mt-4 mb-2">Supplier Information</div>

              <v-row dense>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.seller_name"
                    label="Supplier Name *"
                    outlined
                    dense
                    :error-messages="getValidationError('seller_name')"
                    @input="clearValidationError('seller_name')"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.seller_representative"
                    label="Representative"
                    outlined
                    dense
                    :class="{ 'optional-warning-field': hasOptionalWarning('seller_representative') }"
                    @input="clearOptionalWarning('seller_representative')"
                  />
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="form.seller_address"
                    label="Shipper's address *"
                    outlined
                    dense
                    rows="2"
                    auto-grow
                    :error-messages="getValidationError('seller_address')"
                    @input="clearValidationError('seller_address')"
                  />
                </v-col>

                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="form.seller_phone"
                    label="Phone number"
                    outlined
                    dense
                    :class="{ 'optional-warning-field': hasOptionalWarning('seller_phone') }"
                    @input="clearOptionalWarning('seller_phone')"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="form.seller_fax"
                    label="Fax"
                    outlined
                    dense
                    :class="{ 'optional-warning-field': hasOptionalWarning('seller_fax') }"
                    @input="clearOptionalWarning('seller_fax')"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="form.seller_tax_number"
                    label="Tax number"
                    outlined
                    dense
                    :class="{ 'optional-warning-field': hasOptionalWarning('seller_tax_number') }"
                    @input="clearOptionalWarning('seller_tax_number')"
                  />
                </v-col>

                <v-col cols="12">
                  <v-text-field
                    v-model="form.seller_bank_account"
                    label="Bank account number"
                    outlined
                    dense
                    :class="{ 'optional-warning-field': hasOptionalWarning('seller_bank_account') }"
                    @input="clearOptionalWarning('seller_bank_account')"
                  />
                </v-col>
              </v-row>

              <div class="section-title mt-4 mb-2">Items</div>

              <v-alert
                v-if="getValidationError('items').length"
                dense
                text
                type="warning"
                class="mb-3"
              >
                {{ getValidationError('items')[0] }}
              </v-alert>

              <v-row
                v-for="(item, index) in form.items"
                :key="'contract-item-' + index"
                dense
                class="mb-2"
              >
                <v-col cols="12" md="5">
                  <v-text-field
                    v-model="item.name"
                    label="Name *"
                    outlined
                    dense
                    hide-details="auto"
                    :error-messages="getItemValidationError(index, 'name')"
                    @input="clearItemValidationError(index, 'name')"
                  />
                </v-col>

                <v-col cols="12" md="2">
                  <v-text-field
                    v-model="item.net_unit_price"
                    label="Net price *"
                    outlined
                    dense
                    hide-details="auto"
                    :error-messages="getItemValidationError(index, 'net_unit_price')"
                    @input="clearItemValidationError(index, 'net_unit_price')"
                  />
                </v-col>

                <v-col cols="12" md="2">
                  <v-text-field
                    v-model="item.quantity"
                    label="Unit (db) *"
                    outlined
                    dense
                    hide-details="auto"
                    :error-messages="getItemValidationError(index, 'quantity')"
                    @input="clearItemValidationError(index, 'quantity')"
                  />
                </v-col>

                <v-col cols="12" md="2">
                  <v-text-field
                    v-model="item.net_total"
                    label="Total net"
                    outlined
                    dense
                    hide-details
                    :class="{ 'optional-warning-field': hasItemOptionalWarning(index, 'net_total') }"
                    @input="clearItemOptionalWarning(index, 'net_total')"
                  />
                </v-col>

                <v-col cols="12" md="1" class="d-flex align-center">
                  <v-btn icon color="red" @click="removeItem(index)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </v-col>
              </v-row>

              <v-btn small outlined color="primary" class="mb-4" @click="addItem">
                <v-icon left>mdi-plus</v-icon>
                Add item
              </v-btn>

              <div class="section-title mt-4 mb-2">Summary</div>

              <v-row dense>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="form.net_total"
                    label="Net amount"
                    outlined
                    dense
                    :class="{ 'optional-warning-field': hasOptionalWarning('net_total') }"
                    @input="clearOptionalWarning('net_total')"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="form.vat_total"
                    label="VAT amount"
                    outlined
                    dense
                    :class="{ 'optional-warning-field': hasOptionalWarning('vat_total') }"
                    @input="clearOptionalWarning('vat_total')"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="form.gross_total"
                    label="Gross amount"
                    outlined
                    dense
                    :class="{ 'optional-warning-field': hasOptionalWarning('gross_total') }"
                    @input="clearOptionalWarning('gross_total')"
                  />
                </v-col>
              </v-row>

              <div class="section-title mt-4 mb-2">Contract texts</div>

              <v-row dense>
                <v-col cols="12">
                  <v-textarea
                    v-model="form.delivery_terms_text"
                    label="Shipping"
                    outlined
                    dense
                    rows="2"
                    auto-grow
                    :class="{ 'optional-warning-field': hasOptionalWarning('delivery_terms_text') }"
                    @input="clearOptionalWarning('delivery_terms_text')"
                  />
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="form.payment_method_text"
                    label="Payment method text"
                    outlined
                    dense
                    rows="4"
                    auto-grow
                    :class="{ 'optional-warning-field': hasOptionalWarning('payment_method_text') }"
                    @input="clearOptionalWarning('payment_method_text')"
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-menu
                    v-model="deadlineDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="form.deadline_date"
                        label="Deadline"
                        readonly
                        outlined
                        dense
                        v-bind="attrs"
                        v-on="on"
                        :class="{ 'optional-warning-field': hasOptionalWarning('deadline_date') }"
                      />
                    </template>

                    <v-date-picker
                      v-model="form.deadline_date"
                      no-title
                      scrollable
                      @input="onDeadlineDateSelected"
                    />
                  </v-menu>
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="form.warranty_text"
                    label="Warranty Terms"
                    outlined
                    dense
                    rows="3"
                    auto-grow
                    :class="{ 'optional-warning-field': hasOptionalWarning('warranty_text') }"
                    @input="clearOptionalWarning('warranty_text')"
                  />
                </v-col>
              </v-row>

              <div class="section-title mt-4 mb-2">Legal sections</div>

              <v-alert
                v-if="hasOptionalWarning('legal_clauses')"
                dense
                text
                type="warning"
                class="mb-3"
              >
                Legal sections are empty.
              </v-alert>

              <v-row
                v-for="(clause, index) in form.legal_clauses"
                :key="'clause-' + index"
                dense
                class="mb-2"
              >
                <v-col cols="11">
                  <v-textarea
                    v-model="form.legal_clauses[index]"
                    :label="`Legal Section #${index + 1}`"
                    outlined
                    dense
                    rows="2"
                    auto-grow
                    :class="{ 'optional-warning-field': hasClauseOptionalWarning(index) }"
                    @input="clearClauseOptionalWarning(index)"
                  />
                </v-col>
                <v-col cols="1" class="d-flex align-center justify-center">
                  <v-btn icon color="red" @click="removeClause(index)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </v-col>
              </v-row>

              <v-btn small outlined color="primary" class="mb-4" @click="addClause">
                <v-icon left>mdi-plus</v-icon>
                Add a legal notice
              </v-btn>

              <div class="section-title mt-4 mb-2">Issue</div>

              <v-row dense>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.issue_place"
                    label="Place of issue *"
                    outlined
                    dense
                    :error-messages="getValidationError('issue_place')"
                    @input="clearValidationError('issue_place')"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-menu
                    v-model="issueDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="form.issue_date"
                        label="Issue date *"
                        readonly
                        outlined
                        dense
                        v-bind="attrs"
                        v-on="on"
                        :error-messages="getValidationError('issue_date')"
                      />
                    </template>

                    <v-date-picker
                      v-model="form.issue_date"
                      no-title
                      scrollable
                      @input="onIssueDateSelected"
                    />
                  </v-menu>
                </v-col>
              </v-row>
            </div>
          </div>

          <div v-if="showPreview" class="contract-preview-pane">
            <div class="contract-preview-toolbar">
              <div class="subtitle-2 font-weight-medium">Preview</div>

              <v-progress-circular
                v-if="previewLoading"
                indeterminate
                size="18"
                width="2"
                color="primary"
              />
            </div>

            <iframe
              v-if="previewHtml"
              :srcdoc="previewHtml"
              class="contract-preview-frame"
              frameborder="0"
            />

            <div v-else class="contract-preview-empty">
              No preview.
            </div>
          </div>
        </div>
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-4">
        <v-btn
          text
          color="primary"
          :loading="previewLoading"
          @click="openPreview"
        >
          {{ showPreview ? 'Hide preview' : 'Preview' }}
        </v-btn>

        <v-spacer />

        <v-btn text @click="closeDialog">Cancel</v-btn>

        <v-btn
          color="primary"
          :loading="loading"
          @click="generateDocument"
        >
          Generate
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ApiService from "@/core/services/api.service";
import Swal from "sweetalert2";


const DOCUMENT_TYPE = "sales_contract";

export default {
  name: "SalesContractDialog",

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    salesOrderId: {
      type: [Number, String],
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      previewLoading: false,
      showPreview: false,
      deadlineDateMenu: false,
      issueDateMenu: false,
      previewHtml: "",
      previewDebounceTimer: null,
      previewRequestId: 0,
      validationErrors: {},
      optionalWarnings: {},
      form: {
        contract_number: "",
        customer_name: "",
        customer_address: "",
        shipping_address: "",
        customer_tax_number: "",
        customer_representative: "",
        seller_name: "",
        seller_address: "",
        seller_representative: "",
        seller_phone: "",
        seller_fax: "",
        seller_bank_account: "",
        seller_tax_number: "",
        items: [],
        net_total: "",
        vat_total: "",
        gross_total: "",
        delivery_terms_text: "",
        payment_method_text: "",
        deadline_date: "",
        warranty_text: "",
        legal_clauses: [],
        issue_place: "Zagyvarékas",
        issue_date: "",
      },
    };
  },

  computed: {
    dialogProxy: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },

  watch: {
    value(val) {
      if (val) {
        this.loadDefaults();
      } else {
        this.resetPreview();
      }
    },

    form: {
      deep: true,
      handler() {
        if (!this.showPreview) {
          return;
        }

        this.schedulePreviewRefresh();
      },
    },
  },

  methods: {
    async loadDefaults() {
      try {
        const { data } = await ApiService.get(
          `sales/salesOrder/${this.salesOrderId}/generated-documents/${DOCUMENT_TYPE}/defaults`
        );

        this.form = {
          contract_number: data.contract_number || "",
          customer_name: data.customer_name || "",
          customer_address: data.customer_address || "",
          shipping_address: data.shipping_address || "",
          customer_tax_number: data.customer_tax_number || "",
          customer_representative: data.customer_representative || "",
          seller_name: data.seller_name || "",
          seller_address: data.seller_address || "",
          seller_representative: data.seller_representative || "",
          seller_phone: data.seller_phone || "",
          seller_fax: data.seller_fax || "",
          seller_bank_account: data.seller_bank_account || "",
          seller_tax_number: data.seller_tax_number || "",
          items: Array.isArray(data.items) ? data.items.filter(item => !String(item.name).includes('Szállítási díj')) : [],
          net_total: data.net_total || "",
          vat_total: data.vat_total || "",
          gross_total: data.gross_total || "",
          delivery_terms_text: data.delivery_terms_text || "",
          payment_method_text: data.payment_method_text || "",
          deadline_date: data.deadline_date || "",
          warranty_text: data.warranty_text || "",
          legal_clauses: Array.isArray(data.legal_clauses) ? data.legal_clauses : [],
          issue_place: data.issue_place || "Zagyvarékas",
          issue_date: data.issue_date || "",
        };

        this.validationErrors = {};
        this.optionalWarnings = {};
      } catch (e) {
        console.log(e);
      }
    },

    isEmpty(value) {
      return value === null || value === undefined || String(value).trim() === "";
    },

    hasOptionalWarning(field) {
      return Boolean(this.optionalWarnings[field]);
    },

    hasItemOptionalWarning(index, field) {
      return Boolean(this.optionalWarnings[this.getItemValidationKey(index, field)]);
    },

    hasClauseOptionalWarning(index) {
      return Boolean(this.optionalWarnings[this.getClauseValidationKey(index)]);
    },

    clearOptionalWarning(field) {
      if (this.optionalWarnings[field]) {
        this.$delete(this.optionalWarnings, field);
      }
    },

    clearItemOptionalWarning(index, field) {
      const key = this.getItemValidationKey(index, field);

      if (this.optionalWarnings[key]) {
        this.$delete(this.optionalWarnings, key);
      }
    },

    clearClauseOptionalWarning(index) {
      const key = this.getClauseValidationKey(index);

      if (this.optionalWarnings[key]) {
        this.$delete(this.optionalWarnings, key);
      }
    },

    getValidationError(field) {
      return this.validationErrors[field] || [];
    },

    getItemValidationKey(index, field) {
      return `items.${index}.${field}`;
    },

    getClauseValidationKey(index) {
      return `legal_clauses.${index}`;
    },

    getItemValidationError(index, field) {
      return this.validationErrors[this.getItemValidationKey(index, field)] || [];
    },

    clearValidationError(field) {
      if (this.validationErrors[field]) {
        this.$delete(this.validationErrors, field);
      }
    },

    clearItemValidationError(index, field) {
      const key = this.getItemValidationKey(index, field);

      if (this.validationErrors[key]) {
        this.$delete(this.validationErrors, key);
      }
    },

    validateForm() {
      const errors = {};
      const requiredFields = [
        { key: "customer_name", label: "Customer Name" },
        { key: "customer_address", label: "Billing address" },
        { key: "seller_name", label: "Supplier Name" },
        { key: "seller_address", label: "Shipper's address" },
        { key: "issue_place", label: "Place of issue" },
        { key: "issue_date", label: "Issue date" },
      ];

      requiredFields.forEach((field) => {
        if (this.isEmpty(this.form[field.key])) {
          errors[field.key] = [`${field.label} is required.`];
        }
      });

      if (!Array.isArray(this.form.items) || this.form.items.length === 0) {
        errors.items = ["At least one item is required."];
      } else {
        this.form.items.forEach((item, index) => {
          [
            { key: "name", label: "Item name" },
            { key: "net_unit_price", label: "Net price" },
            { key: "quantity", label: "Unit (db)" },
          ].forEach((field) => {
            if (this.isEmpty(item[field.key])) {
              errors[this.getItemValidationKey(index, field.key)] = [
                `${field.label} is required.`,
              ];
            }
          });
        });
      }

      this.validationErrors = errors;

      return Object.keys(errors).length === 0;
    },

    async showValidationAlert() {
      await Swal.fire({
        title: "Missing required data",
        icon: "warning",
        text: "Please fill in all required fields before creating the document.",
        confirmButtonText: "OK",
      });
    },

    getMissingOptionalFields() {
      const missingFields = [];
      const optionalFields = [
        { key: "contract_number", label: "Contract / SO Number" },
        { key: "customer_tax_number", label: "Customer Tax number" },
        { key: "customer_representative", label: "Customer Representative" },
        { key: "shipping_address", label: "Shipping address" },
        { key: "seller_representative", label: "Supplier Representative" },
        { key: "seller_phone", label: "Supplier Phone number" },
        { key: "seller_fax", label: "Supplier Fax" },
        { key: "seller_tax_number", label: "Supplier Tax number" },
        { key: "seller_bank_account", label: "Bank account number" },
        { key: "net_total", label: "Summary - Net amount" },
        { key: "vat_total", label: "Summary - VAT amount" },
        { key: "gross_total", label: "Summary - Gross amount" },
        { key: "delivery_terms_text", label: "Shipping text" },
        { key: "payment_method_text", label: "Payment method text" },
        { key: "deadline_date", label: "Deadline" },
        { key: "warranty_text", label: "Warranty Terms" },
      ];

      optionalFields.forEach((field) => {
        if (this.isEmpty(this.form[field.key])) {
          missingFields.push(field);
        }
      });

      if (Array.isArray(this.form.items)) {
        this.form.items.forEach((item, index) => {
          if (this.isEmpty(item.net_total)) {
            missingFields.push({
              key: this.getItemValidationKey(index, "net_total"),
              label: `Item ${index + 1} - Total net`,
            });
          }
        });
      }

      if (!Array.isArray(this.form.legal_clauses) || this.form.legal_clauses.length === 0) {
        missingFields.push({
          key: "legal_clauses",
          label: "Legal sections",
        });
      } else {
        this.form.legal_clauses.forEach((clause, index) => {
          if (this.isEmpty(clause)) {
            missingFields.push({
              key: this.getClauseValidationKey(index),
              label: `Legal Section #${index + 1}`,
            });
          }
        });
      }

      return missingFields;
    },

    markOptionalWarnings(fields) {
      const warnings = {};

      fields.forEach((field) => {
        warnings[field.key] = true;
      });

      this.optionalWarnings = warnings;
    },

    async confirmMissingOptionalFields() {
      const missingFields = this.getMissingOptionalFields();

      if (!missingFields.length) {
        this.optionalWarnings = {};
        return true;
      }

      const fieldList = missingFields
        .map((field) => `<li>${field.label}</li>`)
        .join("");

      const result = await Swal.fire({
        title: "Some fields are empty",
        icon: "warning",
        html: `The following fields are not filled:<br><ul style="text-align:left;margin-top:12px;">${fieldList}</ul><br>Do you want to continue anyway?`,
        showCancelButton: true,
        confirmButtonText: "Yes, generate document",
        cancelButtonText: "No, I will fill them",
        confirmButtonColor: "#00c300",
        width: "650px",
      });

      if (result.isConfirmed) {
        this.optionalWarnings = {};
        return true;
      }

      this.markOptionalWarnings(missingFields);
      return false;
    },

    onDeadlineDateSelected() {
      this.deadlineDateMenu = false;
      this.clearOptionalWarning("deadline_date");
    },

    onIssueDateSelected() {
      this.issueDateMenu = false;
      this.clearValidationError("issue_date");
    },

    addItem() {
      this.clearValidationError("items");

      this.form.items.push({
        name: "",
        net_unit_price: "",
        quantity: 1,
        net_total: "",
      });
    },

    removeItem(index) {
      this.form.items.splice(index, 1);
    },

    addClause() {
      this.clearOptionalWarning("legal_clauses");

      this.form.legal_clauses.push("");
    },

    removeClause(index) {
      this.form.legal_clauses.splice(index, 1);
    },

    resetPreview() {
      this.showPreview = false;
      this.previewHtml = "";
      this.previewLoading = false;
      this.validationErrors = {};
      this.optionalWarnings = {};

      if (this.previewDebounceTimer) {
        clearTimeout(this.previewDebounceTimer);
        this.previewDebounceTimer = null;
      }
    },

    closeDialog() {
      this.resetPreview();
      this.dialogProxy = false;
    },

    schedulePreviewRefresh() {
      if (this.previewDebounceTimer) {
        clearTimeout(this.previewDebounceTimer);
      }

      this.previewDebounceTimer = setTimeout(() => {
        this.refreshPreview();
      }, 400);
    },

    async openPreview() {
      if(this.showPreview === true){
        this.showPreview = false;
      }else{
        this.showPreview = true;
      }
      
      await this.refreshPreview();
    },

    async refreshPreview() {
      const requestId = ++this.previewRequestId;
      this.previewLoading = true;

      try {
        const { data } = await ApiService.post(
          `sales/salesOrder/${this.salesOrderId}/generated-documents/${DOCUMENT_TYPE}/preview`,
          this.form
        );

        if (requestId !== this.previewRequestId) {
          return;
        }

        this.previewHtml = data.html || "";
      } catch (error) {
        console.log(error);
      } finally {
        if (requestId === this.previewRequestId) {
          this.previewLoading = false;
        }
      }
    },

    async generateDocument() {
      if (!this.validateForm()) {
        await this.showValidationAlert();
        return;
      }

      const missingFields = this.getMissingOptionalFields();

      this.markOptionalWarnings(missingFields);

      const canContinue = await this.confirmMissingOptionalFields();

      if (!canContinue) {
        return;
      }

      this.loading = true;

      try {
        const { data } = await ApiService.post(
          `sales/salesOrder/${this.salesOrderId}/generated-documents/${DOCUMENT_TYPE}/generate`,
          this.form
        );

        if (data && data.url) {
          window.open(data.url, "_blank");
        }

        this.$emit("generated", data);
        this.closeDialog();
      } catch (error) {
        console.error(error);

        Swal.fire({
          title: "Error",
          icon: "error",
          text: "The document could not be created.",
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.contract-dialog-layout {
  display: flex;
  min-height: 70vh;
}

.contract-form-pane {
  flex: 0 0 100%;
  max-width: 100%;
  border-right: 1px solid #e0e0e0;
  overflow-y: auto;
}
.contract-form-pane.with-preview {
  flex: 0 0 40%;
  max-width: 40%;
}

.contract-preview-pane {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  background: #f5f5f5;
  padding: 16px;
  min-width: 0;
}

.contract-preview-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.contract-preview-frame {
  width: 100%;
  height: calc(70vh - 32px);
  background: #fff;
  border: 1px solid #dcdcdc;
  border-radius: 8px;
}

.contract-preview-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: calc(70vh - 32px);
  background: #fff;
  border: 1px solid #dcdcdc;
  border-radius: 8px;
  color: #757575;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
}

.optional-warning-field >>> .v-input__slot {
  background-color: #fff8e1 !important;
}

.optional-warning-field >>> fieldset {
  border-color: #f9a825 !important;
}
</style>